import { Button } from 'design-system';
import { Moon as LucideMoon, Sun } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Metallic as MetallicIcon } from '../../../design-system/icons';
import { request } from '../../../service/request';

const NightModeToggle = ({
  onToggle,
  className = '',
  btnVariant = 'text',
  btnColor,
  style,
  ...rest
}) => {
  const user = useSelector((state) => state.user?.user);
  const userId = user?.id;
  const [isNightMode, setIsNightMode] = useState(() => {
    const storedTheme = localStorage.getItem('theme');
    return storedTheme === 'dark';
  });

  const removeClassTimeout = useRef();

  useLayoutEffect(() => {
    if (!localStorage.getItem('theme') && user) {
      const userTheme = user.preferred_theme || user.theme;
      if (userTheme) setIsNightMode(userTheme === 'dark');
    }
  }, [user]);

  useEffect(() => {
    const html = document.documentElement;
    const next = isNightMode ? 'dark' : 'light';
    html.setAttribute('data-theme', next);
    // Also toggle Tailwind/utility dark mode class for components that rely on it
    try {
      html.classList.toggle('dark', next === 'dark');
    } catch {}
    localStorage.setItem('theme', next);
    if (onToggle) onToggle(isNightMode);
  }, [isNightMode, onToggle]);

  const handleClick = () => {
    const body = document.body;
    body.classList.add('transition-theme');
    const newMode = !isNightMode;
    // Apply theme immediately so it persists even if popover closes/unmounts
    try {
      const html = document.documentElement;
      const next = newMode ? 'dark' : 'light';
      html.setAttribute('data-theme', next);
      try {
        html.classList.toggle('dark', next === 'dark');
      } catch {}
      localStorage.setItem('theme', next);
      if (onToggle) onToggle(newMode);
    } catch {}
    setIsNightMode(newMode);
    clearTimeout(removeClassTimeout.current);
    removeClassTimeout.current = setTimeout(() => {
      body.classList.remove('transition-theme');
    }, 2000);
    if (userId) {
      request(`/users/${userId}/night_mode`, { night_mode: newMode }, 'put').catch((e) =>
        console.error('Failed to sync night mode', e),
      );
    }
  };

  const iconNode = isNightMode ? (
    <MetallicIcon Icon={Sun} variant="silver" strokeWidth={2.4} size={18} aria-hidden />
  ) : (
    <LucideMoon size={18} strokeWidth={2} aria-hidden />
  );

  return (
    <Button
      size="small"
      variant={btnVariant}
      color={btnColor}
      onClick={handleClick}
      icon={iconNode}
      className={className}
      style={style}
      {...rest}
    />
  );
};

export default NightModeToggle;
